import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore'
import CHAMPIONSHIPS from '../data/championships'
import { getMatchTitleValues } from './matchTitles'

export const TITLE_DEFENSE_BONUS_VALUES = new Set(['mantiene_titolo'])
export const TITLE_WIN_BONUS_VALUES = new Set([
  'vince_titolo_tag',
  'vince_titolo_singolo',
  'vince_titolo_massimo',
  'doppio_campione'
])
export const TITLE_REIGN_MILESTONE_BONUS_DAYS = new Map([
  ['campione_100', 100],
  ['campione_200', 200],
  ['campione_280', 280],
  ['campione_365', 365]
])

const TITLE_EVENT_BONUS_VALUES = new Set([
  ...TITLE_DEFENSE_BONUS_VALUES,
  ...TITLE_WIN_BONUS_VALUES,
  ...TITLE_REIGN_MILESTONE_BONUS_DAYS.keys()
])

const WORLD_TITLE_VALUES = new Set([
  'WWE Champion',
  'World Heavyweight Champion',
  "WWE Women's Champion",
  "Women's World Champion",
  'NXT Champion',
  "NXT Women's Champion",
  'TNA World Champion',
  'TNA Knockouts World Champion',
  'AAA Mega Champion',
  'AAA Reina de Reinas Champion'
])

const CHAMPIONSHIP_BY_VALUE = new Map(
  CHAMPIONSHIPS.map(championship => [championship.value, championship])
)

const toDateValue = value => {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value?.toDate === 'function') return value.toDate()
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000)
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getShowDateValue = showData =>
  toDateValue(showData?.data || showData?.date || showData?.timestamp)

const normalizeStoredBonusValue = value =>
  String(value || '').replace(/_ext$/, '')

const getMatchParticipantIds = match => {
  const participantIds = Array.isArray(match?.participants)
    ? match.participants
    : []
  const teamParticipantIds = Array.isArray(match?.teams)
    ? match.teams.flatMap(team =>
        Array.isArray(team?.participants) ? team.participants : []
      )
    : []

  return [...new Set([...participantIds, ...teamParticipantIds].filter(Boolean))]
}

const getLatestDate = values => {
  const validDates = values.filter(
    value => value instanceof Date && !Number.isNaN(value.getTime())
  )

  if (validDates.length === 0) return null

  return validDates.reduce((latest, current) =>
    current.getTime() > latest.getTime() ? current : latest
  )
}

const getElapsedDays = (startDate, now = new Date()) => {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return 0

  const daysDiff = Math.floor(
    (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  )

  return daysDiff > 0 ? daysDiff : 1
}

const subtractDays = (dateValue, daysToSubtract) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return null
  }

  return new Date(
    dateValue.getTime() - Number(daysToSubtract || 0) * 24 * 60 * 60 * 1000
  )
}

const buildEmptyTitleStat = () => ({
  hasData: false,
  wonAt: null,
  defendedAt: null,
  daysAsChampion: 0,
  daysSinceLastDefense: 0
})

const getAcquisitionBonusValuesForTitle = titleValue => {
  const title = CHAMPIONSHIP_BY_VALUE.get(titleValue)

  if (!title) {
    return new Set(['doppio_campione'])
  }

  if (
    title.type === 'tag_team' ||
    title.type === 'mixed_tag_team' ||
    title.type === 'trios'
  ) {
    return new Set(['vince_titolo_tag', 'doppio_campione'])
  }

  if (WORLD_TITLE_VALUES.has(titleValue)) {
    return new Set(['vince_titolo_massimo', 'doppio_campione'])
  }

  return new Set(['vince_titolo_singolo', 'doppio_campione'])
}

export const extractShowIdFromRefPath = refPath => {
  const parts = String(refPath || '').split('/').filter(Boolean)
  if (parts[0] !== 'shows' || !parts[1]) return null
  return parts[1]
}

export const hasTrackedTitleBonus = participation => {
  const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : []
  return bonuses.some(bonus =>
    TITLE_EVENT_BONUS_VALUES.has(normalizeStoredBonusValue(bonus))
  )
}

const getLatestMilestoneCandidate = candidates => {
  const validCandidates = candidates.filter(
    candidate =>
      candidate?.showDate instanceof Date &&
      !Number.isNaN(candidate.showDate.getTime()) &&
      candidate?.inferredWinDate instanceof Date &&
      !Number.isNaN(candidate.inferredWinDate.getTime())
  )

  if (validCandidates.length === 0) return null

  return validCandidates.reduce((selected, current) => {
    if (current.showDate.getTime() > selected.showDate.getTime()) {
      return current
    }

    if (current.showDate.getTime() < selected.showDate.getTime()) {
      return selected
    }

    return current.inferredWinDate.getTime() < selected.inferredWinDate.getTime()
      ? current
      : selected
  })
}

export const buildTitleStatsByTitle = ({
  wrestlerId,
  titleValues = [],
  participations = [],
  matchesByShowId = new Map(),
  showDatesByShowId = new Map(),
  now = new Date()
}) => {
  const normalizedTitleValues = [...new Set(titleValues.filter(Boolean))]
  const titleValueSet = new Set(normalizedTitleValues)
  const acquisitionBonusesByTitle = new Map(
    normalizedTitleValues.map(titleValue => [
      titleValue,
      getAcquisitionBonusValuesForTitle(titleValue)
    ])
  )

  const titleDates = normalizedTitleValues.reduce((acc, titleValue) => {
    acc[titleValue] = {
      exactWins: [],
      exactDefenses: [],
      exactMilestones: [],
      fallbackWins: [],
      fallbackDefenses: [],
      fallbackMilestones: []
    }
    return acc
  }, {})

  participations.forEach(participation => {
    const bonuses = Array.isArray(participation?.bonuses)
      ? participation.bonuses.map(normalizeStoredBonusValue)
      : []

    const hasWin = bonuses.some(bonus => TITLE_WIN_BONUS_VALUES.has(bonus))
    const hasDefense = bonuses.some(bonus =>
      TITLE_DEFENSE_BONUS_VALUES.has(bonus)
    )
    const milestoneBonusDays = bonuses
      .map(bonus => TITLE_REIGN_MILESTONE_BONUS_DAYS.get(bonus))
      .filter(days => Number.isFinite(days) && days > 0)
    const hasMilestone = milestoneBonusDays.length > 0

    if (!hasWin && !hasDefense && !hasMilestone) return

    const showId = extractShowIdFromRefPath(participation?.refPath)
    if (!showId) return

    const showDate = showDatesByShowId.get(showId)
    if (!(showDate instanceof Date) || Number.isNaN(showDate.getTime())) return

    const relatedTitleMatches = (matchesByShowId.get(showId) || []).filter(
      match =>
        getMatchTitleValues(match).some(titleValue => titleValueSet.has(titleValue)) &&
        getMatchParticipantIds(match).includes(wrestlerId)
    )

    normalizedTitleValues.forEach(titleValue => {
      const titleBucket = titleDates[titleValue]
      const acquisitionBonusValues = acquisitionBonusesByTitle.get(titleValue)
      const hasAcquisitionForTitle = bonuses.some(bonus =>
        acquisitionBonusValues.has(bonus)
      )
      const hasExactTitleMatch = relatedTitleMatches.some(
        match => getMatchTitleValues(match).includes(titleValue)
      )

      if (!hasAcquisitionForTitle && !hasDefense && !hasMilestone) return

      if (hasAcquisitionForTitle) {
        titleBucket.fallbackWins.push(showDate)
      }

      if (hasDefense) {
        titleBucket.fallbackDefenses.push(showDate)
      }

      if (hasMilestone) {
        milestoneBonusDays.forEach(milestoneDays => {
          const inferredWinDate = subtractDays(showDate, milestoneDays)
          if (!inferredWinDate) return

          titleBucket.fallbackMilestones.push({
            showDate,
            inferredWinDate
          })
        })
      }

      if (!hasExactTitleMatch) return

      if (hasAcquisitionForTitle) {
        titleBucket.exactWins.push(showDate)
      }

      if (hasDefense) {
        titleBucket.exactDefenses.push(showDate)
      }

      if (hasMilestone) {
        milestoneBonusDays.forEach(milestoneDays => {
          const inferredWinDate = subtractDays(showDate, milestoneDays)
          if (!inferredWinDate) return

          titleBucket.exactMilestones.push({
            showDate,
            inferredWinDate
          })
        })
      }
    })
  })

  return normalizedTitleValues.reduce((acc, titleValue) => {
    const {
      exactWins,
      exactDefenses,
      exactMilestones,
      fallbackWins,
      fallbackDefenses,
      fallbackMilestones
    } =
      titleDates[titleValue]
    const latestWin = getLatestDate(exactWins) || getLatestDate(fallbackWins)
    const latestMilestone =
      getLatestMilestoneCandidate(exactMilestones) ||
      getLatestMilestoneCandidate(fallbackMilestones)
    const latestDefense =
      getLatestDate(exactDefenses) || getLatestDate(fallbackDefenses)
    const effectiveWinDate = latestWin || latestMilestone?.inferredWinDate || null
    const latestDefenseAfterWin = effectiveWinDate
      ? getLatestDate(
          [...exactDefenses, ...fallbackDefenses].filter(
            defenseDate => defenseDate >= effectiveWinDate
          )
        )
      : latestDefense

    const effectiveDefenseDate = effectiveWinDate
      ? latestDefenseAfterWin || effectiveWinDate
      : latestDefense || null

    acc[titleValue] = {
      hasData: Boolean(effectiveWinDate || effectiveDefenseDate),
      wonAt: effectiveWinDate,
      defendedAt: effectiveDefenseDate,
      daysAsChampion: getElapsedDays(effectiveWinDate, now),
      daysSinceLastDefense: getElapsedDays(effectiveDefenseDate, now)
    }

    return acc
  }, normalizedTitleValues.reduce((acc, titleValue) => {
    acc[titleValue] = buildEmptyTitleStat()
    return acc
  }, {}))
}

export const loadTitleStatsForWrestlers = async ({
  db,
  wrestlerIds = [],
  titleValuesByWrestler = {},
  now = new Date()
}) => {
  const normalizedWrestlerIds = [...new Set(wrestlerIds.filter(Boolean))]

  if (normalizedWrestlerIds.length === 0) return {}

  const wrestlerIdSet = new Set(normalizedWrestlerIds)
  const allParticipationsSnap = await getDocs(collectionGroup(db, 'participations'))
  const participationsByWrestler = normalizedWrestlerIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = []
    return acc
  }, {})

  allParticipationsSnap.docs.forEach(participationDoc => {
    const participation = {
      id: participationDoc.id,
      ...participationDoc.data(),
      refPath: participationDoc.ref.path
    }

    if (!wrestlerIdSet.has(participation.wrestlerId)) return
    if (!hasTrackedTitleBonus(participation)) return

    participationsByWrestler[participation.wrestlerId].push(participation)
  })

  const participationsByWrestlerEntries = Object.entries(participationsByWrestler)

  const showIds = [
    ...new Set(
      participationsByWrestlerEntries.flatMap(([, participations]) =>
        participations
          .map(participation => extractShowIdFromRefPath(participation.refPath))
          .filter(Boolean)
      )
    )
  ]

  const showEntries = await Promise.all(
    showIds.map(async showId => {
      const [showSnap, matchesSnap] = await Promise.all([
        getDoc(doc(db, 'shows', showId)),
        getDocs(collection(db, 'shows', showId, 'matches'))
      ])

      return {
        showId,
        showData: showSnap.exists() ? showSnap.data() : {},
        matches: matchesSnap.docs.map(matchDoc => ({
          id: matchDoc.id,
          ...matchDoc.data()
        }))
      }
    })
  )

  const showDatesByShowId = new Map(
    showEntries.map(entry => [entry.showId, getShowDateValue(entry.showData)])
  )
  const matchesByShowId = new Map(
    showEntries.map(entry => [entry.showId, entry.matches])
  )

  return normalizedWrestlerIds.reduce((acc, wrestlerId) => {
    acc[wrestlerId] = buildTitleStatsByTitle({
      wrestlerId,
      titleValues: titleValuesByWrestler[wrestlerId] || [],
      participations: participationsByWrestler[wrestlerId] || [],
      matchesByShowId,
      showDatesByShowId,
      now
    })
    return acc
  }, {})
}
